import React from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ArcElement,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false, // We handle labels manually where needed
    },
    tooltip: {
      backgroundColor: '#fff',
      titleColor: '#1e293b',
      bodyColor: '#1e293b',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
    },
  },
};

export const EmployeeBarChart: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  const sortedEntries = Object.entries(data)
    .map(([name, value]) => ({ name, hours: parseFloat(value.toFixed(2)) }))
    .sort((a, b) => b.hours - a.hours);

  const chartData = {
    labels: sortedEntries.map((item) => item.name),
    datasets: [
      {
        label: 'Hours',
        data: sortedEntries.map((item) => item.hours),
        backgroundColor: '#0ea5e9',
        borderRadius: { topLeft: 6, topRight: 6 },
        borderSkipped: 'bottom',
      },
    ],
  };

  const options = {
    ...commonOptions,
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 13, weight: 500 }, color: '#64748b' },
        border: { color: '#e2e8f0' },
      },
      y: {
        grid: { drawBorder: false, color: '#f1f5f9', drawTicks: false },
        ticks: { font: { size: 13 }, color: '#64748b' },
        border: { color: '#e2e8f0' },
        title: {
          display: true,
          text: 'Hours',
          color: '#64748b',
          font: { size: 12 },
          padding: { top: 10 },
          align: 'center' as const,
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', height: '280px' }}>
      <Bar data={chartData as any} options={options as any} />
    </div>
  );
};

export const StatusPieChart: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  const entries = Object.entries(data);
  const chartData = {
    labels: entries.map(([name]) => name),
    datasets: [
      {
        data: entries.map(([, value]) => value),
        backgroundColor: COLORS,
        borderWidth: 0,
      },
    ],
  };

  const options = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      tooltip: {
        ...commonOptions.plugins.tooltip,
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${percentage}%`;
          },
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', height: '280px' }}>
      <Pie data={chartData} options={options} />
    </div>
  );
};

export const TaskDonutChart: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  const entries = Object.entries(data);
  const chartData = {
    labels: entries.map(([name]) => name),
    datasets: [
      {
        data: entries.map(([, value]) => value),
        backgroundColor: COLORS,
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  const options = {
    ...commonOptions,
    cutout: '60%', // Makes it a donut
    plugins: {
      ...commonOptions.plugins,
      tooltip: {
        ...commonOptions.plugins.tooltip,
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${percentage}%`;
          },
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', height: '280px' }}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

export const DailyLineChart: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  const sortedEntries = Object.entries(data)
    .map(([date, hours]) => ({
      date,
      hours: parseFloat(hours.toFixed(2)),
      sortDate: date.split('-').reverse().join('-'), // Convert DD-MM-YYYY → YYYY-MM-DD for sorting
    }))
    .sort((a, b) => new Date(a.sortDate).getTime() - new Date(b.sortDate).getTime());

  const chartData = {
    labels: sortedEntries.map((item) => item.date),
    datasets: [
      {
        label: 'Hours',
        data: sortedEntries.map((item) => item.hours),
        borderColor: '#0ea5e9',
        backgroundColor: 'rgba(14, 165, 233, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#0ea5e9',
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        tension: 0.4, // Smooth curve (monotone equivalent)
      },
    ],
  };

  const options = {
    ...commonOptions,
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 13, weight: 500 }, color: '#64748b' },
        border: { color: '#e2e8f0' },
      },
      y: {
        grid: { color: '#f1f5f9' },
        ticks: { font: { size: 13 }, color: '#64748b' },
        border: { color: '#e2e8f0' },
        title: {
          display: true,
          text: 'Hours',
          color: '#64748b',
          font: { size: 12 },
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', height: '280px' }}>
      <Line data={chartData} options={options as any} />
    </div>
  );
};